import React, { useEffect, useState, useCallback } from "react";
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  ShieldCheck,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { api } from "../../lib/api";

/* ── Visual grammar ────────────────────────────────────────────────────
   This is operational mission-control, not a CRM feed. Single calm
   strip, generous spacing, dot + numeric chip, no badge soup.

   Momentum: improving (emerald) · stable (slate) · softening (amber)
   Blockers: ROSE only when blocking>0; otherwise muted slate.

   Inspired by Linear's "pulse" rows + Stripe's calm operational
   summaries. Strictly NO Facebook-style activity language.
*/

const MOMENTUM_TONE = {
  improving: {
    Icon: TrendingUp,
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-200/70",
    dot: "bg-emerald-500",
    label: "Momentum improving",
  },
  softening: {
    Icon: TrendingDown,
    color: "text-amber-800",
    bg: "bg-amber-50",
    border: "border-amber-200/70",
    dot: "bg-amber-500",
    label: "Progression slowed",
  },
  stable: {
    Icon: Minus,
    color: "text-slate-700",
    bg: "bg-white",
    border: "border-slate-200",
    dot: "bg-slate-400",
    label: "Operationally stable",
  },
};

/**
 * OperationalVitals — calm momentum strip rendered above the timeline.
 *
 * Single horizontal strip. Three reading rhythms:
 *   1. Momentum icon + headline sentence
 *   2. Blockers chip (only rendered when > 0)
 *   3. "Today" group chips ("3 progression updates today")
 *
 * Stateless: every poll hits /timeline/summary, server derives from
 * the underlying activity rows. No client-side caching beyond a
 * 60-second refetch interval to keep the strip honest.
 */
export const OperationalVitals = ({ dealId, refreshMs = 60000 }) => {
  const [vitals, setVitals] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!dealId) return;
    try {
      const { data } = await api.get(`/api/v2/deals/${dealId}/timeline/summary`);
      setVitals(data);
      setError(null);
    } catch (e) {
      setError(e?.message);
    }
  }, [dealId]);

  useEffect(() => {
    load();
    if (!refreshMs) return undefined;
    const t = setInterval(load, refreshMs);
    return () => clearInterval(t);
  }, [load, refreshMs]);

  if (vitals === null && !error) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3" data-testid="vitals-loading">
        <div className="flex items-center gap-2 text-slate-500 text-[12px]">
          <Loader2 size={13} className="animate-spin" /> Reading operational pulse…
        </div>
      </div>
    );
  }

  if (error) return null;            // silent failure — never block the timeline render
  if (!vitals) return null;

  const tone = MOMENTUM_TONE[vitals.momentum_summary] || MOMENTUM_TONE.stable;
  const MomentumIcon = tone.Icon;

  return (
    <div
      className={`rounded-2xl border ${tone.border} ${tone.bg} px-4 py-3.5`}
      data-testid="operational-vitals"
    >
      <div className="flex items-center gap-5 flex-wrap">
        {/* Momentum heading -------------------------------------------- */}
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className={`w-7 h-7 rounded-lg flex items-center justify-center bg-white border ${tone.border} ${tone.color} shrink-0`}
            aria-hidden="true"
          >
            <MomentumIcon size={13} />
          </span>
          <div className="min-w-0">
            <p
              className={`text-[12.5px] font-bold ${tone.color} truncate`}
              data-testid="vitals-headline"
            >
              {vitals.headline}
            </p>
            <p className="text-[10.5px] text-slate-500 mt-0.5 uppercase tracking-[0.14em] font-semibold">
              <span data-testid="vitals-momentum">{tone.label}</span>
              {Number.isFinite(vitals.net_confidence) && vitals.net_confidence !== 0 && (
                <span className="text-slate-400 ml-1.5">
                  · {vitals.net_confidence > 0 ? "+" : ""}{vitals.net_confidence} pts (24h)
                </span>
              )}
            </p>
          </div>
        </div>

        <span className="h-7 w-px bg-slate-200/70 hidden sm:block" aria-hidden="true" />

        {/* Blockers chip ----------------------------------------------- */}
        <BlockerChip
          outstanding={vitals.blockers_outstanding}
          blocking={vitals.blockers_blocking}
          warning={vitals.blockers_warning}
        />

        {/* Today's groups ---------------------------------------------- */}
        {vitals.groups_today?.length > 0 && (
          <div
            className="flex items-center gap-2 flex-wrap ml-auto"
            data-testid="vitals-groups"
          >
            <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400">
              Today
            </p>
            {vitals.groups_today.slice(0, 4).map((g) => (
              <span
                key={g.tag}
                data-testid={`vitals-group-${g.tag}`}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white border border-slate-200 text-[10.5px] font-semibold text-slate-700"
              >
                <span className="cf-num text-slate-900">{g.count}</span>
                <span className="text-slate-500 capitalize">{g.label}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const BlockerChip = ({ outstanding, blocking, warning }) => {
  if (!outstanding) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700 font-semibold"
        data-testid="vitals-blockers-none"
      >
        <ShieldCheck size={11} /> No blockers
      </span>
    );
  }
  const isBlocking = blocking > 0;
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10.5px] font-semibold border " +
        (isBlocking
          ? "bg-rose-50 text-rose-700 border-rose-200"
          : "bg-amber-50 text-amber-800 border-amber-200")
      }
      data-testid="vitals-blockers-chip"
    >
      <AlertTriangle size={11} />
      <span className="cf-num">{outstanding}</span>
      <span>{isBlocking ? "blocking" : "outstanding"}</span>
      {warning > 0 && blocking > 0 && (
        <span className="text-slate-500 font-normal">· {warning} slowing</span>
      )}
    </span>
  );
};

export default OperationalVitals;
